import React from "react";

import * as dates from "@client/dates";
import {assign} from "@client/utils";

import {BasicModal} from "@client/components/Modal";
import type {Action, AppDispatch} from "@client/store";

export {HModal, BasicModal} from "@client/components/Modal";

export type Actions =
    | {
          type: "SHOW_ALERT_DIALOG";
          message: string;
          body: string;
      }
    | {
          type: "HIDE_ALERT_DIALOG";
      }
    | {
          type: "SHOW_CONFIRM_DIALOG";
          message: string;
          body: string;
      }
    | {
          type: "CONFIRM_CONFIRM_DIALOG";
      }
    | {
          type: "HIDE_CONFIRM_DIALOG";
      }
    | {
          type: "SHOW_DATE_DIALOG";
          focusOnDate: DateValue;
      }
    | {
          type: "PICK_DATE_FOR_DATE_DIALOG";
          pickedDate: DateValue;
      }
    | {
          type: "HIDE_DATE_DIALOG";
      };

const initialState = {
    isShowingAlertDialog: false,
    isShowingConfirmDialog: false,
    isShowingDateDialog: false as DateValue | false,
    didConfirmDialog: false,
    pickedDate: null as DateValue | null,
    message: "",
    body: "",
};

export const reducer = (state = initialState, action: Action) => {
    switch (action.type) {
        case "SHOW_ALERT_DIALOG": {
            return assign(initialState, {
                isShowingAlertDialog: true,
                message: action.message,
                body: action.body,
            });
        }
        case "SHOW_CONFIRM_DIALOG": {
            return assign(initialState, {
                isShowingConfirmDialog: true,
                message: action.message,
                body: action.body,
            });
        }
        case "CONFIRM_CONFIRM_DIALOG": {
            return assign(initialState, {didConfirmDialog: true});
        }
        case "SHOW_DATE_DIALOG": {
            return assign(initialState, {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- eslint's project-service program disagrees with tsc here: removing this assertion fails compilation. Verdict is consistent on all platforms under stableTypeOrdering.
                isShowingDateDialog: action.focusOnDate as DateValue | false,
            });
        }
        case "PICK_DATE_FOR_DATE_DIALOG": {
            return assign(initialState, {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- see above.
                pickedDate: action.pickedDate as DateValue | null,
            });
        }
        case "HIDE_DATE_DIALOG":
        case "HIDE_ALERT_DIALOG":
        case "HIDE_CONFIRM_DIALOG": {
            return initialState;
        }
        default: {
            return state;
        }
    }
};

let resolveAlert: (() => void) | null = null;

export const alert =
    ({message, body}: {message: string; body: string}) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: "SHOW_ALERT_DIALOG",
            message,
            body,
        });
        return new Promise<void>((resolve) => {
            resolveAlert = resolve;
        });
    };

export const hideAlertDialog = () => (dispatch: AppDispatch) => {
    dispatch({
        type: "HIDE_ALERT_DIALOG",
    });
    resolveAlert?.();
    resolveAlert = null;
};

let resolveConfirm: ((confirmed: boolean) => void) | null = null;

export const confirm =
    ({message, body}: {message: string; body: string}) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: "SHOW_CONFIRM_DIALOG",
            message,
            body,
        });
        return new Promise<boolean>((resolve) => {
            resolveConfirm = resolve;
        });
    };

export const confirmConfirmDialog = () => (dispatch: AppDispatch) => {
    dispatch({
        type: "CONFIRM_CONFIRM_DIALOG",
    });
    resolveConfirm?.(true);
    resolveConfirm = null;
};

export const hideConfirmDialog = () => (dispatch: AppDispatch) => {
    dispatch({
        type: "HIDE_CONFIRM_DIALOG",
    });
    resolveConfirm?.(false);
    resolveConfirm = null;
};

let resolveDatePicker: ((date: DateValue | null) => void) | null = null;

export const pickDate = (focusOnDate?: DateValue) => (dispatch: AppDispatch) => {
    dispatch({
        type: "SHOW_DATE_DIALOG",
        focusOnDate:
            focusOnDate ?? dates.formatISO(new Date(), {representation: "date"}),
    });

    return new Promise<DateValue | null>((resolve) => {
        resolveDatePicker = resolve;
    });
};

export const pickDateForDateDialog = (dateValue: Date) => (dispatch: AppDispatch) => {
    const pickedDate = dates.formatISO(dateValue, {representation: "date"});
    dispatch({
        type: "PICK_DATE_FOR_DATE_DIALOG",
        pickedDate,
    });
    resolveDatePicker?.(pickedDate);
    resolveDatePicker = null;
};

export const hideDateDialog = () => (dispatch: AppDispatch) => {
    dispatch({
        type: "HIDE_DATE_DIALOG",
    });
    resolveDatePicker?.(null);
    resolveDatePicker = null;
};

// Possible useful: https://archive.is/ZpwDt
export function useDialog<T, TInput = undefined>(options: {
    Content: (
        props: (TInput extends undefined ? Record<never, never> : TInput) & {
            close: (result: T | null) => void;
        },
    ) => React.ReactNode;
    // Only for HMR
    isOpen?: boolean; // This is buggy! Should actually require the inputs to be open by default, otherwise the inptus will be undefined.
    modal?: typeof BasicModal;
    // When true, memoizes content based on input only (not isOpen), preserving
    // internal state during close animation. Migrate dialogs incrementally.
    untestedIsOpenMigrateIfWorkingOnThisComponent?: boolean;
}) {
    type TActualInput = TInput extends undefined ? Record<never, never> : TInput;
    const resolvePromise = React.useRef<((result: T | null) => void) | null>(null);
    const [isOpen, setIsOpen] = React.useState(options.isOpen ?? false);

    const [input, setInput] = React.useState<TActualInput | null>(null);

    const close = (result: T | null) => {
        setIsOpen(false);
        resolvePromise.current?.(result);
    };
    const Component = options.Content;

    const handler = (
        ...args: TInput extends undefined ? [] : [Omit<TInput, "close">]
    ) => {
        return new Promise<T | null>((resolve) => {
            resolvePromise.current = resolve;

            setInput(args[0] as TActualInput);
            setIsOpen(true);
        });
    };
    const ModalComponent = options.modal ?? BasicModal;

    // When flag is true, memoize based on input only to preserve state during close animation
    const content = React.useMemo(
        () => <Component {...(input as TActualInput)} close={close} />,

        options.untestedIsOpenMigrateIfWorkingOnThisComponent == true
            ? [input]
            : [isOpen],
    );

    handler.dialog = (
        <ModalComponent isOpen={isOpen} setOpen={() => close(null)}>
            {content}
        </ModalComponent>
    );

    return handler;
}
